import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Check, Edit, EllipsisVertical, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  displayErrorToast,
  useRefreshCharacterDetails,
} from 'app/frontend/base/helpers'
import { updateCharacter } from 'app/frontend/base/api/character'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { removeTeamMember } from 'app/frontend/base/api/team'
import { toast } from 'sonner'
import {
  getRolesWithMultipleSpecs,
  getSpecializationsByClass,
} from 'app/frontend/base/services/characterValues'
import { Link } from 'react-router-dom'
import { updateMember } from 'app/frontend/base/api/teamMember'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import capitalize from 'lodash/capitalize'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { CharacterRole } from 'app/frontend/types/custom'
import { Checkbox } from '@/components/ui/checkbox'
import CharacterAvatar from 'app/frontend/base/components/CharacterAvatar'
import { CharacterClassIcon } from 'app/frontend/base/components'
import { usePageInfo } from 'app/frontend/base/config/queries'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

type Props = {
  member: Partial<wowaudit.TeamMembership>
  size?: 'small' | 'medium'
}

const TeamMemberContextMenu = ({ member, size = 'medium' }: Props) => {
  const { t } = useTranslation()
  const { guild } = useTeam()
  const {
    data: { user },
  } = usePageInfo()
  const [open, setOpen] = useState(false)

  const { mutate: removeFromTeam, isPending: removing } = useMutation({
    mutationFn: (memberId: number) => removeTeamMember(team.id, memberId),
    onSuccess: (newData) => {
      toast.success(
        t('dashboard.team.roster.member-removed', {
          name: member.characterReference?.name,
        }),
      )
      queryClient.setQueryData(['team', { path: team.path }], newData)
      setOpen(false)
    },
    onError: (error) => {
      displayErrorToast(error, t)
    },
  })

  const queryClient = useQueryClient()
  const { team } = useTeam()
  const { refreshing, refreshData } = useRefreshCharacterDetails(
    member.characterReference,
    {
      retriever: updateCharacter,
      id: member.characterReference?.id,
      successKey: 'dashboard.character.character-update-success',
      callback: () => {
        queryClient.invalidateQueries({
          queryKey: ['teamCharacterDetails', { id: team.id }],
        })
        queryClient.invalidateQueries({
          queryKey: ['team', { path: team.path }],
        })
        setOpen(false)
      },
    },
  )

  const { mutate: updateTeamMember, isPending: isUpdating } = useMutation({
    mutationFn: (data: Partial<wowaudit.TeamMembership>) =>
      updateMember(team.id, member.id as number, data),
    onSuccess: (newData) => {
      toast.success(
        t('dashboard.team.roster.member-updated', {
          name: member.characterReference?.name,
        }),
      )
      queryClient.setQueryData(['team', { path: team.path }], newData)
    },
    onError: (error) => {
      displayErrorToast(error, t)
    },
  })

  const handleRankChange = (rankId: number) => {
    if (member.teamRankId === rankId) return
    updateTeamMember({ teamRankId: rankId })
    setOpen(false)
  }

  const handleRoleChange = (role: CharacterRole) => {
    if (member.role === role && member.specId === null) return
    updateTeamMember({ role, specId: null })
    setOpen(false)
  }

  const handleSpecChange = (specId: number) => {
    if (member.specId === specId) return
    updateTeamMember({ specId })
    setOpen(false)
  }

  const handleOffSpecChange = (specId: number) => {
    const offspecIds = member.offspecIds || []
    updateTeamMember({
      offspecIds: offspecIds.includes(specId)
        ? offspecIds.filter((id) => id !== specId)
        : [...offspecIds, specId],
    })
    setOpen(false)
  }

  const classSpecializations = getSpecializationsByClass(
    member.characterReference?.classId,
  )
  const rolesWithMultipleSpecs = getRolesWithMultipleSpecs(
    member.characterReference?.classId,
  )

  const canRefreshManually =
    user?.canRefreshManually || guild?.canRefreshManually

  const RefreshMenuItem = (
    <DropdownMenuItem
      disabled={refreshing || !canRefreshManually}
      onClick={(e) => {
        e.preventDefault()
        if (refreshing) return
        refreshData()
      }}
    >
      {refreshing ? (
        <span className='flex items-center gap-2'>
          <Loader2 className='w-4 h-4 animate-spin' />
          {t('shared.refreshing')}
        </span>
      ) : (
        t('dashboard.team.roster.refresh-details')
      )}
    </DropdownMenuItem>
  )

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger disabled={isUpdating}>
        <div className='flex items-center justify-center cursor-pointer h-5'>
          {isUpdating ? (
            <Loader2
              className={`${
                size === 'small' ? 'w-3 h-3' : 'w-5 h-5'
              } animate-spin`}
            />
          ) : (
            <EllipsisVertical size={size === 'small' ? 12 : 18} />
          )}
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align='end'
        alignOffset={-10}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuLabel>
          {member.characterReference ? (
            <div className='flex items-center gap-2'>
              <CharacterAvatar
                character={member.characterReference}
                className='h-8 w-8'
              />

              <div>
                <Link
                  to={member.characterReference.path}
                  className='flex items-center gap-1.5'
                >
                  <CharacterClassIcon
                    characterClass={member.characterReference.characterClass}
                    size='small'
                  />
                  {member.characterReference.name}
                </Link>

                <div className='opacity-70 font-normal text-[0.7rem] mt-[-0.2rem]'>
                  {member.characterReference.realm.name} (
                  <span>{member.characterReference.realm.region}</span>)
                </div>
              </div>
            </div>
          ) : null}

          <div className='opacity-70 italic font-normal mt-2'>
            Added {member.trackingRuleId ? 'automatically' : 'manually'}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <div className='flex flex-col items-start mt-[-0.1rem]'>
              <span className='grow'>Set rank</span>
              <span className='text-xs text-muted-foreground'>
                {
                  team.teamRanks?.find((rank) => rank.id === member.teamRankId)
                    ?.name
                }
              </span>
            </div>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              {team.teamRanks?.map((rank) => (
                <DropdownMenuItem
                  key={rank.id}
                  onClick={() => handleRankChange(rank.id)}
                >
                  <div
                    className={`flex items-center gap-2 justify-between w-full ${
                      member.teamRankId === rank.id ? 'text-primary' : ''
                    }`}
                  >
                    <span>{rank.name}</span>
                    {member.teamRankId === rank.id && (
                      <Check className='w-4 h-4' />
                    )}
                  </div>
                </DropdownMenuItem>
              ))}

              <DropdownMenuSeparator />

              <Link to={`${team.path}/settings#roster`}>
                <DropdownMenuItem>
                  <span className='flex items-center gap-2 opacity-70'>
                    <Edit className='w-4 h-4' /> Manage ranks
                  </span>
                </DropdownMenuItem>
              </Link>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <div className='flex flex-col items-start mt-[-0.1rem]'>
              <span className='grow'>Set main spec</span>

              {member.specId ? (
                <span className='text-xs text-muted-foreground flex items-center gap-1.5'>
                  <BlizzardIcon
                    iconName={classSpecializations[member.specId].icon}
                    size='small'
                  />
                  {classSpecializations[member.specId].name}
                </span>
              ) : (
                <span className='text-xs text-muted-foreground flex items-center gap-1.5'>
                  <CharacterRoleIcon
                    role={member.role ?? 'melee'}
                    size='small'
                  />
                  {capitalize(member.role)}
                </span>
              )}
            </div>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              {rolesWithMultipleSpecs?.map((role) => (
                <DropdownMenuItem
                  key={role}
                  onClick={() => handleRoleChange(role as CharacterRole)}
                >
                  <div
                    className={`flex items-center gap-2 w-full ${
                      member.role === role && member.specId === null
                        ? 'text-primary'
                        : ''
                    }`}
                  >
                    <CharacterRoleIcon
                      role={role as CharacterRole}
                      className='mr-8'
                    />
                    <span>{capitalize(role)}</span>
                  </div>
                </DropdownMenuItem>
              ))}

              {Object.entries(classSpecializations).map(([specId, spec]) => (
                <DropdownMenuItem
                  key={specId}
                  onClick={() => handleSpecChange(Number(specId))}
                >
                  <div
                    className={`flex items-center gap-2 w-full ${
                      member.specId === Number(specId) ? 'text-primary' : ''
                    }`}
                  >
                    <CharacterRoleIcon role={spec.default_role} />
                    <BlizzardIcon iconName={spec.icon} />
                    <span>{spec.name}</span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        {!member.specId &&
        Object.values(classSpecializations).every(
          (spec) => spec.default_role === member.role,
        ) ? null : (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <div className='flex flex-col items-start mt-[-0.1rem]'>
                <span className='grow'>Set off specs</span>
                <span className='text-xs text-muted-foreground'>
                  {member.offspecIds?.length ? (
                    <div className='flex items-center gap-1.5 flex-wrap'>
                      {member.offspecIds?.map((specId) => (
                        <BlizzardIcon
                          key={specId}
                          iconName={classSpecializations[specId].icon}
                          size='small'
                        />
                      ))}
                    </div>
                  ) : null}
                </span>
              </div>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                {Object.entries(classSpecializations).map(([specId, spec]) => {
                  if (
                    member.specId === Number(specId) ||
                    (member.role === spec.default_role &&
                      member.specId === null)
                  )
                    return null

                  return (
                    <DropdownMenuItem
                      key={specId}
                      onClick={() => handleOffSpecChange(Number(specId))}
                    >
                      <div
                        className={`flex items-center gap-2 w-full ${
                          member.offspecIds?.includes(Number(specId))
                            ? 'text-primary'
                            : 'text-muted-foreground'
                        }`}
                      >
                        <CharacterRoleIcon role={spec.default_role} />
                        <BlizzardIcon iconName={spec.icon} />
                        <div className='grow'>{spec.name}</div>
                        <Checkbox
                          className='ml-2 border-accent bg-card-nested data-[state=checked]:border-primary'
                          checked={member.offspecIds?.includes(Number(specId))}
                        />
                      </div>
                    </DropdownMenuItem>
                  )
                })}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        )}

        {canRefreshManually ? (
          RefreshMenuItem
        ) : (
          <Tooltip>
            <TooltipTrigger className='cursor-not-allowed'>
              {RefreshMenuItem}
            </TooltipTrigger>
            <TooltipContent className='flex flex-col gap-1'>
              <p>
                Refreshing on demand is only available for users with a
                subscription tier of at least{' '}
                <span className='font-semibold text-quality-epic'>Epic</span>.
              </p>
              <p>
                This character will update on a schedule automatically (check
                the refresh status on the Audit page).
              </p>
            </TooltipContent>
          </Tooltip>
        )}

        {member.id && (
          <DropdownMenuItem
            disabled={removing}
            onClick={(e) => {
              e.preventDefault()
              if (removing) return
              removeFromTeam(member.id as number)
            }}
          >
            {removing ? (
              <span className='flex items-center gap-2'>
                <Loader2 className='w-4 h-4 animate-spin' />
                {t('shared.removing')}
              </span>
            ) : (
              <span className='text-destructive-foreground'>
                {member.trackingRuleId
                  ? t('dashboard.team.roster.remove-and-blacklist')
                  : t('shared.remove')}
              </span>
            )}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default TeamMemberContextMenu
